import React, { useState, useEffect, useContext } from "react";
import { ThemeProvider } from "styled-components";

import EmptyState from "../../../components/empty-state/EmptyState";
import Overview from "./overview/Overview";
import AnnouncementsEditor from "../../../features/announcements/components/editor/AnnouncementsEditor";
import ButtonEditor from "../../../features/button-block/components/editor/ButtonEditor";
import CardsEditor from "../../../features/cards-v2/components/editor/CardsEditor";
import PanelSimpleEditor from "../../../features/panel-simple/components/editor/PanelSimpleEditor";
import CountdownEditor from "../../../features/countdown/components/editor/CountdownEditor";
import SpacerAndDividerEditor from "../../../features/spacer-and-divider/components/editor/SpacerAndDividerEditor";
import TitleAndTextEditor from "../../../features/title-and-text/components/editor/TitleAndTextEditor";
import ImageSliderEditor from "../../../features/image-slider-new/components/editor/ImageSliderEditor";
import AdvancedTabsGroupEditor from "../../../features/advanced-tabs/group/components/editor/AdvancedTabsGroupEditor";
import FootnoteDisplayEditor from "../../../features/footnote-display/editor/FootnoteDisplayEditor";
import TooltipFootnoteEditor from "../../../features/tooltip-and-footnote/editor/TooltipFootnoteEditor";
import AdvancedTabsSingleEditor from "../../../features/advanced-tabs/single/components/editor/AdvancedTabsSingleEditor";
import PanelAdvancedEditor from "../../../features/panel-advanced/components/editor/PanelAdvancedEditor";
import ButtonInlineEditor from "../../../features/button-inline/editor/ButtonInlineEditor";
import EasyHTMLEditor from "../../../features/easy-html/editor/EasyHTMLEditor";
import PeopleEditor from "../../../features/people/edit/PeopleEditor";
import NewsEditor from "../../../features/news/edit/NewsEditor";
import PagesEditor from "../../../features/pages/edit/PagesEditor";
import SpacesEditor from "../../../features/spaces/edit/SpacesEditor";
import RecentlyActiveEditor from "../../../features/recently-active/edit/RecentlyActiveEditor";
import Toolbar from "../toolbar/Toolbar";
import Columns from "../toolbar/components/Columns";
import GlobalUndoRedo from "../toolbar/components/GlobalUndoRedo";
import GroupCards from "../toolbar/components/GroupCards";
import ModeSwitcher from "../drawers/settings/features/html/ModeSwitcher";
import CosmosState from "../../../components/cosmos-state/CosmosState";
import UpcomingBirthdayEditor from "../../../features/upcoming-birthday/edit/UpcomingBirthdayEditor";
import NewStartersEditor from "../../../features/new-starters/edit/NewStartersEditor";
import WorkAnniversaryEditor from "../../../features/work-anniversary/edit/WorkAnniversaryEditor";
import OrgChartEditor from "../../../features/org-chart/edit/OrgChartEditor";
import useWidgetSettingsByKey from "../../../hooks/use-widget-settings-by-key";
import { MACRO_KEY } from "../../defaults";
import { ContentContainer } from "./styles";
import { EMPTY_STATE_KEY } from "../../../components/empty-state/templates";
import { macroStores } from "../../../stores/export-stores";
import { useOpticsStore } from "../../../stores/use-optics-store";
import { MACROS_REQUIRE_COSMOS } from "../sidebar/defaults";
import { WIDGET_COSMOS_IDS } from "../editor/defaults";
import { colorSchemeContext } from "../../../context/colorSchemeContext";

const editorComponents = {
  [MACRO_KEY.OVERVIEW_KEY]: Overview,

  [MACRO_KEY.BUTTON]: ButtonEditor,
  [MACRO_KEY.BUTTON_INLINE]: ButtonInlineEditor,
  [MACRO_KEY.CARDS]: CardsEditor,
  [MACRO_KEY.PANEL_SIMPLE]: PanelSimpleEditor,
  [MACRO_KEY.ANNOUNCEMENTS]: AnnouncementsEditor,
  [MACRO_KEY.COUNTDOWN]: CountdownEditor,
  [MACRO_KEY.SPACER_AND_DIVIDER]: SpacerAndDividerEditor,
  [MACRO_KEY.TEXT_AND_TITLE]: TitleAndTextEditor,
  [MACRO_KEY.IMAGE_SLIDER]: ImageSliderEditor,
  [MACRO_KEY.ADVANCED_TABS_GROUP]: AdvancedTabsGroupEditor,
  [MACRO_KEY.FOOTNOTE_DISPLAY]: FootnoteDisplayEditor,
  [MACRO_KEY.TOOLTIPS_AND_FOOTNOTES]: TooltipFootnoteEditor,
  [MACRO_KEY.ADVANCED_TABS_SINGLE]: AdvancedTabsSingleEditor,
  [MACRO_KEY.PANEL_ADVANCED]: PanelAdvancedEditor,
  [MACRO_KEY.HTML]: EasyHTMLEditor,
  [MACRO_KEY.PEOPLE]: PeopleEditor,
  [MACRO_KEY.NEWS]: NewsEditor,
  [MACRO_KEY.PAGES]: PagesEditor,
  [MACRO_KEY.SPACES]: SpacesEditor,
  [MACRO_KEY.ORG_CHART]: OrgChartEditor,
  [MACRO_KEY.RECENTLY_ACTIVE]: RecentlyActiveEditor,
  [MACRO_KEY.UPCOMING_BIRTHDAYS]: UpcomingBirthdayEditor,
  [MACRO_KEY.NEW_STARTERS]: NewStartersEditor,
  [MACRO_KEY.WORK_ANNIVERSARY]: WorkAnniversaryEditor,
};

const toolbarComponents = {
  [MACRO_KEY.BUTTON]: {
    ThirdComponent: GlobalUndoRedo,
  },
  [MACRO_KEY.BUTTON_INLINE]: {
    ThirdComponent: GlobalUndoRedo,
  },
  [MACRO_KEY.CARDS]: {
    FirstComponent: GroupCards,
    SecondComponent: Columns,
    ThirdComponent: GlobalUndoRedo,
  },
  [MACRO_KEY.PANEL_SIMPLE]: {
    ThirdComponent: GlobalUndoRedo,
  },
  [MACRO_KEY.ANNOUNCEMENTS]: {
    SecondComponent: Columns,
    ThirdComponent: GlobalUndoRedo,
  },
  [MACRO_KEY.COUNTDOWN]: {
    ThirdComponent: GlobalUndoRedo,
  },
  [MACRO_KEY.SPACER_AND_DIVIDER]: {
    ThirdComponent: GlobalUndoRedo,
  },
  [MACRO_KEY.TEXT_AND_TITLE]: {
    ThirdComponent: GlobalUndoRedo,
  },
  [MACRO_KEY.IMAGE_SLIDER]: {
    ThirdComponent: GlobalUndoRedo,
  },
  [MACRO_KEY.ADVANCED_TABS_GROUP]: {
    ThirdComponent: GlobalUndoRedo,
  },
  [MACRO_KEY.ADVANCED_TABS_SINGLE]: {
    ThirdComponent: GlobalUndoRedo,
  },
  [MACRO_KEY.PANEL_ADVANCED]: {
    ThirdComponent: GlobalUndoRedo,
  },
  [MACRO_KEY.HTML]: {
    SecondComponent: ModeSwitcher,
  },
  [MACRO_KEY.NEWS]: {
    FirstComponent: GroupCards,
    SecondComponent: Columns,
    ThirdComponent: GlobalUndoRedo,
  },
  [MACRO_KEY.PAGES]: {
    FirstComponent: GroupCards,
    SecondComponent: Columns,
    ThirdComponent: GlobalUndoRedo,
  },
  [MACRO_KEY.SPACES]: {
    FirstComponent: GroupCards,
    SecondComponent: Columns,
    ThirdComponent: GlobalUndoRedo,
  },
  [MACRO_KEY.PEOPLE]: {
    FirstComponent: GroupCards,
    SecondComponent: Columns,
    ThirdComponent: GlobalUndoRedo,
  },
  [MACRO_KEY.RECENTLY_ACTIVE]: {
    SecondComponent: Columns,
    ThirdComponent: GlobalUndoRedo,
  },
  [MACRO_KEY.UPCOMING_BIRTHDAYS]: {
    SecondComponent: Columns,
    ThirdComponent: GlobalUndoRedo,
  },
  [MACRO_KEY.NEW_STARTERS]: {
    SecondComponent: Columns,
    ThirdComponent: GlobalUndoRedo,
  },
  [MACRO_KEY.WORK_ANNIVERSARY]: {
    SecondComponent: Columns,
    ThirdComponent: GlobalUndoRedo,
  },
};

const Content = () => {
  const [contentColorScheme, setContentColorScheme] = useState();

  const { colorScheme, mergedThemes } = useContext(colorSchemeContext);

  const { macro, isCosmosInstalled } = useOpticsStore((state) => ({
    macro: state.macro,
    isCosmosInstalled: state.isCosmosInstalled,
  }));

  const { widgetSettingsData } = useWidgetSettingsByKey({
    enabled: isCosmosInstalled && MACROS_REQUIRE_COSMOS.includes(macro),
    key: macro,
    widgetId: WIDGET_COSMOS_IDS[macro],
  });

  const {
    exists = false,
    widget: { selectedField: selectedDatabaseFieldId = "" } = {},
  } = widgetSettingsData;

  const widgetIsSetup = selectedDatabaseFieldId && exists;

  useEffect(() => {
    window.AP.events.on("theme-changed", (value) => {
      if (value) {
        setContentColorScheme(value.name);
      }
    });
  }, []);

  const macroHasData = macroStores[macro]?.((state) => {
    const data = state?.data || {};
    return Array.isArray(data) ? !!data.length : !!Object.keys(data).length;
  });

  const EditorComponent =
    editorComponents[macro] ||
    (() => <EmptyState template={EMPTY_STATE_KEY.GETTING_STARTED} />);

  const toolbarProps = toolbarComponents[macro] || {};

  const isOverviewMacro = macro === MACRO_KEY.OVERVIEW_KEY;

  const isCosmosWidget = MACROS_REQUIRE_COSMOS.includes(macro);

  const cosmosValidation = isCosmosWidget
    ? isCosmosInstalled && widgetIsSetup
    : true;

  const displayConditions = {
    toolbar: macroHasData && cosmosValidation && !isOverviewMacro,
    editor:
      (macroHasData || macro === MACRO_KEY.OVERVIEW_KEY) && cosmosValidation,
    emptyState: !isOverviewMacro && !macroHasData,
    cosmosEmptyState: isCosmosWidget
      ? !widgetIsSetup || !isCosmosInstalled
      : false,
  };

  return (
    <ThemeProvider theme={mergedThemes[contentColorScheme || colorScheme]}>
      <ContentContainer>
        {displayConditions.toolbar && <Toolbar {...toolbarProps} />}

        {displayConditions.editor && <EditorComponent />}

        {displayConditions.emptyState &&
          !displayConditions.cosmosEmptyState && (
            <EmptyState template={EMPTY_STATE_KEY.NO_TEMPLATE} />
          )}

        {displayConditions.cosmosEmptyState && (
          <CosmosState
            isCosmosInstalled={isCosmosInstalled}
            isCosmosSetup={widgetIsSetup}
            macro={macro}
          />
        )}
      </ContentContainer>
    </ThemeProvider>
  );
};

export default Content;
